import { Badge } from '@fluentui/react-components';

// Maps a status string to a Fluent Badge with the AirBridge status palette.
// Used by both the migrations list (migration status) and the device grid (device
// status); strings are normalized to lowercase before matching so variations like
// "Completed", "complete", "COMPLETED" all land on the same colour.
//
// Device lifecycle (FD-190 / FD-197): pending -> initialized -> in-progress ->
// one of the four FA-ADR-021 terminal outcomes. Before FD-190 a device's Status
// was frozen at `initialized` forever, so the terminal values never reached this
// component and fell through to a neutral grey badge — a faulted device looked
// benign in the device grid.
//
//   completed              -> success     (green) — migrated cleanly
//   faulted / failed       -> danger      (red)   — the genuine failure
//   aborted / salvaged     -> warning     (amber) — non-success terminal, needs
//                                                   an operator; NOT a crash
//   in-progress / running  -> informative (sky)   — underway
//   pending / initialized  -> subtle      (zinc)  — not started
//
// Why amber for aborted/salvaged rather than red: per FA-ADR-021 these are
// *designed* recovery outcomes ("abort-and-salvage as one mechanism" — make the
// device usable, set the recovery admin, flag loudly), not crashes. Colouring
// them red alongside `faulted` would erase the triage distinction an operator
// needs at a glance: red = broken, amber = look at this, green = fine. This
// follows the amber-for-needs-attention precedent already set by
// BprtStatusBadge's `expired`.
const COMPLETED = ['completed', 'complete'];
const FAILED = ['faulted', 'failed', 'error'];
const ATTENTION = ['aborted', 'salvaged'];
const RUNNING = ['inprogress', 'in-progress', 'in progress', 'running'];

export default function StatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();

  if (COMPLETED.includes(normalized)) {
    return (
      <Badge appearance="tint" color="success">
        {status}
      </Badge>
    );
  }
  if (FAILED.includes(normalized)) {
    return (
      <Badge appearance="tint" color="danger">
        {status}
      </Badge>
    );
  }
  if (ATTENTION.includes(normalized)) {
    return (
      <Badge appearance="tint" color="warning">
        {status}
      </Badge>
    );
  }
  if (RUNNING.includes(normalized)) {
    return (
      <Badge appearance="tint" color="informative">
        {status}
      </Badge>
    );
  }
  // Unknown/other value (incl. pending, initialized, and migration-level
  // statuses like `active`) — surface it verbatim, neutral.
  return (
    <Badge appearance="tint" color="subtle">
      {status}
    </Badge>
  );
}
